<template>
  <div class="entry titled-document">
    <div id="scrollable-document">
      <div class="infos">
        <TitledDocument
          ref="document"
          :value="value"
          :editable-title="false"
          :debounce="5000"
          :templates="journalTemplates"
          :loading-notes="isLoading"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  PropType,
  provide,
  ref,
  toRefs,
  watch,
} from "vue";

import { useDocument } from "@/components/document";
import * as inject from "@/components/inject";
import { journalTemplates } from "@/components/templates";
import TitledDocument from "@/components/TitledDocument.vue";
import type { EntryRecord } from "@/composables/useJournalEntry";
import { useScrollable } from "@/composables/useScrollable";

export default defineComponent({
  name: "Entry",
  components: {
    TitledDocument,
  },
  props: {
    value: {
      type: Object as PropType<EntryRecord>,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { value: entry } = toRefs(props);
    const router = inject.router();
    const document = ref<InstanceType<typeof TitledDocument> | null>(null);

    const scrollable = useScrollable(["scrollable-document"], "manual");
    const { restore } = scrollable;
    onMounted(scrollable.mount);
    onBeforeUnmount(scrollable.unmount);

    // Provide parent context for child components
    // Use kind "object" so task creation recognizes Journal as a custom type
    const parent = computed(() =>
      entry.value.id ? { kind: "object" as const, id: entry.value.id } : null
    );
    provide("parent", parent);

    // Provide objectId for child components (task creation, etc.)
    const objectId = computed(() => entry.value.id);
    inject.objectId(objectId);

    watch(
      entry,
      (newEntry) => {
        if (newEntry?.id) {
          // Restore scrolling position once the entry is fully loaded.
          restore();
        }
      },
      { deep: true, immediate: true }
    );

    return {
      journalTemplates,
      document,
      ...useDocument<EntryRecord>(entry, document, router),
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/event.scss";

.entry {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: visible;
  // For the rectangle selection in the editor
  position: relative;

  #scrollable-document {
    width: 100%;
    box-sizing: border-box;
    display: flex;
    overflow-y: scroll;
    scrollbar-width: none;
  }

  .infos {
    width: 100%;
    min-height: 100%;
    display: flex;
    flex-direction: column;
    // Make room for the prosemirror-after-content click area
    padding-bottom: 50vh;
  }
}
</style>
